import { forwardRef, useId } from 'react';
import * as Label from '@radix-ui/react-label';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
  Text input with a label, optional hint and optional error, wired together properly.

  What "properly" means here, because this is the part most component libraries get almost
  right:

  * The label is a real <label> bound by htmlFor, so clicking it focuses the input and a
    screen reader reads the name.
  * hint and error ids are both listed in aria-describedby, in that order, so both are
    announced rather than the error replacing the hint.
  * aria-invalid is set from the error, not from a validation library, so the state a
    screen reader hears and the state the user sees cannot diverge.
  * The error text is rendered in a live region so a server-side validation failure that
    arrives after submit is announced without moving focus.
*/

export interface FieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  /** Visually hide the label but keep it for assistive technology. */
  readonly labelHidden?: boolean | undefined;
  readonly trailing?: React.ReactNode | undefined;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, labelHidden = false, trailing, className, required, ...rest },
  ref,
) {
  const uid = useId();
  const inputId = `field-${uid}`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <Label.Root
        htmlFor={inputId}
        className={cn(
          'text-xs font-semibold tracking-wide text-fg-muted uppercase',
          labelHidden && 'sr-only',
        )}
      >
        {label}
        {required === true ? (
          <span className="ml-1 text-[var(--color-danger)]" aria-hidden="true">
            *
          </span>
        ) : null}
      </Label.Root>

      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          className={cn(
            'h-10 w-full rounded-[var(--radius-control)] border bg-surface px-3 text-sm text-fg',
            'placeholder:text-fg-subtle',
            'transition-colors',
            error === undefined
              ? 'border-border hover:border-border-strong'
              : 'border-[var(--color-danger)]',
            trailing !== undefined && 'pr-10',
            className,
          )}
          {...rest}
        />
        {trailing !== undefined ? (
          <span className="absolute top-1/2 right-2 -translate-y-1/2">{trailing}</span>
        ) : null}
      </div>

      {hint !== undefined ? (
        <p id={hintId} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}

      <p
        id={errorId}
        role="alert"
        aria-live="polite"
        className={cn(
          'flex items-start gap-1.5 text-xs font-medium text-[var(--color-danger)]',
          error === undefined && 'hidden',
        )}
      >
        {error === undefined ? null : (
          <>
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </>
        )}
      </p>
    </div>
  );
});
